import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faCog,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";

const SystemSettingsManagement = () => {
  const [activeTab, setActiveTab] = useState("system_settings"); // 'system_settings' or 'conversion_rates'
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);

  // Currency conversion rates state
  const [conversionRates, setConversionRates] = useState([]);
  const [conversionRatesLoading, setConversionRatesLoading] = useState(false);
  const [showConversionRateModal, setShowConversionRateModal] = useState(false);
  const [editingConversionRate, setEditingConversionRate] = useState(null);
  const [conversionRateFormData, setConversionRateFormData] = useState({
    year: new Date().getFullYear(),
    from_currency: "USD",
    to_currency: "CAD",
    conversion_rate: 1.35,
    is_active: true,
  });
  const [formData, setFormData] = useState({
    setting_name: "",
    setting_key: "",
    setting_value: "",
    setting_type: "",
    description: "",
    unit: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [settingTypeFilter, setSettingTypeFilter] = useState("");

  const getSettingByKeys = (...keys) =>
    settings.find((setting) => keys.includes(setting.settingKey));

  const defaultCurrencySetting = getSettingByKeys(
    "default_currency",
    "currency",
  );
  const preferredUnitSetting = getSettingByKeys(
    "preferred_unit",
    "preferred_units",
  );

  const handleQuickSettingUpdate = async (setting, settingValue) => {
    if (!setting) return;
    try {
      await api.put(`/system-settings/${setting.id}`, {
        setting_value: settingValue,
      });
      fetchSettings();
    } catch (err) {
      setError("Failed to update setting");
      console.error("Error updating quick setting:", err);
    }
  };
  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(settingTypeFilter && { setting_type: settingTypeFilter }),
      });

      const response = await api.get(`/system-settings?${params}`);
      if (response.data.success) {
        setSettings(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError("Failed to fetch system settings");
      console.error("Error fetching settings:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, settingTypeFilter]);

  // Fetch conversion rates
  const fetchConversionRates = async () => {
    try {
      setConversionRatesLoading(true);
      const response = await api.get("/currency-conversion-rates");
      if (response.data.success) {
        setConversionRates(response.data.data || []);
      }
    } catch (err) {
      setError("Failed to fetch currency conversion rates");
      console.error("Error fetching conversion rates:", err);
    } finally {
      setConversionRatesLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (activeTab === "conversion_rates") {
      fetchConversionRates();
    }
  }, [activeTab]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSetting) {
        await api.put(`/system-settings/${editingSetting.id}`, formData);
      } else {
        await api.post("/system-settings", formData);
      }

      setShowModal(false);
      setEditingSetting(null);
      setFormData({
        setting_name: "",
        setting_key: "",
        setting_value: "",
        setting_type: "",
        description: "",
        unit: "",
      });
      fetchSettings();
    } catch (err) {
      setError("Failed to save system setting");
      console.error("Error saving setting:", err);
    }
  };

  // Handle edit
  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setFormData({
      setting_name: setting.settingName,
      setting_key: setting.settingKey,
      setting_value: setting.settingValue,
      setting_type: setting.settingType,
      description: setting.description || "",
      unit: setting.unit || "",
      is_active: setting.isActive,
    });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this system setting?")
    ) {
      try {
        await api.delete(`/system-settings/${id}`);
        fetchSettings();
      } catch (err) {
        setError("Failed to delete system setting");
        console.error("Error deleting setting:", err);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      setting_name: "",
      setting_key: "",
      setting_value: "",
      setting_type: "",
      description: "",
      unit: "",
    });
    setEditingSetting(null);
  };

  // Get setting type color
  const getSettingTypeColor = (type) => {
    const colors = {
      conversion_rate: "bg-blue-100 text-blue-800",
      multiplier: "bg-green-100 text-green-800",
      proportion: "bg-purple-100 text-purple-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[type] || "bg-orange-100 text-orange-800";
  };

  // Format setting value for display
  const formatSettingValue = (value, unit) => {
    if (unit) {
      return `${value} ${unit}`;
    }
    return value;
  };

  // Currency conversion rate handlers
  const handleConversionRateSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConversionRate) {
        await api.put(
          `/currency-conversion-rates/${editingConversionRate.id}`,
          conversionRateFormData,
        );
      } else {
        await api.post("/currency-conversion-rates", conversionRateFormData);
      }
      setShowConversionRateModal(false);
      setEditingConversionRate(null);
      setConversionRateFormData({
        year: new Date().getFullYear(),
        from_currency: "USD",
        to_currency: "CAD",
        conversion_rate: 1.35,
        is_active: true,
      });
      fetchConversionRates();
    } catch (err) {
      setError("Failed to save currency conversion rate");
      console.error("Error saving conversion rate:", err);
    }
  };

  const handleConversionRateEdit = (rate) => {
    setEditingConversionRate(rate);
    setConversionRateFormData({
      year: rate.year,
      from_currency: rate.fromCurrency || rate.from_currency,
      to_currency: rate.toCurrency || rate.to_currency,
      conversion_rate: rate.conversionRate || rate.conversion_rate,
      is_active:
        rate.isActive !== undefined ? rate.isActive : rate.is_active !== false,
    });
    setShowConversionRateModal(true);
  };

  const handleConversionRateDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this conversion rate?")
    ) {
      try {
        await api.delete(`/currency-conversion-rates/${id}`);
        fetchConversionRates();
      } catch (err) {
        setError("Failed to delete conversion rate");
        console.error("Error deleting conversion rate:", err);
      }
    }
  };

  const getConversionRateIsActive = (rate) =>
    rate.isActive !== undefined ? rate.isActive : rate.is_active !== false;

  const handleConversionRateToggleActive = async (rate) => {
    try {
      await api.put(`/currency-conversion-rates/${rate.id}`, {
        is_active: !getConversionRateIsActive(rate),
      });
      fetchConversionRates();
    } catch (err) {
      setError("Failed to update conversion rate status");
      console.error("Error updating conversion rate status:", err);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          System Settings Management
        </h1>
        <p className="text-gray-600">
          Manage system-wide settings including conversion rates, multipliers,
          and proportions
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("system_settings")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "system_settings"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            System Settings
          </button>
          <button
            onClick={() => setActiveTab("conversion_rates")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "conversion_rates"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Currency Conversion Rates
          </button>
        </nav>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* System Settings Tab Content */}
      {activeTab === "system_settings" && (
        <>
          {/* Filters and Search */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search settings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={settingTypeFilter}
              onChange={(e) => setSettingTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="conversion_rate">Conversion Rate</option>
              <option value="multiplier">Multiplier</option>
              <option value="proportion">Proportion</option>
              <option value="other">Other</option>
            </select>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
              Add Setting
            </button>
          </div>

          {(defaultCurrencySetting || preferredUnitSetting) && (
            <div className="mb-6 bg-white border border-gray-200 rounded-md p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Currency
                  </label>
                  <select
                    value={defaultCurrencySetting?.settingValue || "CAD"}
                    onChange={(e) =>
                      handleQuickSettingUpdate(
                        defaultCurrencySetting,
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!defaultCurrencySetting}
                  >
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Unit
                  </label>
                  <select
                    value={preferredUnitSetting?.settingValue || "m²"}
                    onChange={(e) =>
                      handleQuickSettingUpdate(
                        preferredUnitSetting,
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!preferredUnitSetting}
                  >
                    <option value="m²">m²</option>
                    <option value="ft²">ft²</option>
                    <option value="sqm">sqm</option>
                    <option value="sqft">sqft</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {loading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading settings...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Setting Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Key
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {settings.map((setting) => (
                      <tr key={setting.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={faCog}
                              className="h-4 w-4 text-gray-400 mr-2"
                            />
                            {setting.settingName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {setting.settingKey}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            {setting.settingType === "conversion_rate" && (
                              <FontAwesomeIcon
                                icon={faDollarSign}
                                className="h-4 w-4 text-green-600 mr-1"
                              />
                            )}
                            <span className="font-medium">
                              {formatSettingValue(
                                setting.settingValue,
                                setting.unit,
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSettingTypeColor(setting.settingType)}`}
                          >
                            {setting.settingType
                              .replace("_", " ")
                              .toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {setting.description || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(setting)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="h-4 w-4"
                              />
                            </button>
                            <button
                              onClick={() => handleDelete(setting.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="h-4 w-4"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page - 1 })
                  }
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page + 1 })
                  }
                  disabled={pagination.page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total,
                      )}
                    </span>{" "}
                    of <span className="font-medium">{pagination.total}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() =>
                        setPagination({
                          ...pagination,
                          page: pagination.page - 1,
                        })
                      }
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {Array.from(
                      { length: pagination.pages },
                      (_, i) => i + 1,
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => setPagination({ ...pagination, page })}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() =>
                        setPagination({
                          ...pagination,
                          page: pagination.page + 1,
                        })
                      }
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Currency Conversion Rates Tab Content */}
      {activeTab === "conversion_rates" && (
        <>
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => {
                setEditingConversionRate(null);
                setConversionRateFormData({
                  year: new Date().getFullYear(),
                  from_currency: "USD",
                  to_currency: "CAD",
                  conversion_rate: 1.35,
                  is_active: true,
                });
                setShowConversionRateModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
              Add Conversion Rate
            </button>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {conversionRatesLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">
                  Loading conversion rates...
                </p>
              </div>
            ) : conversionRates.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p>No conversion rates found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Year
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        To
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {conversionRates.map((rate) => (
                      <tr key={rate.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {rate.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {rate.fromCurrency || rate.from_currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {rate.toCurrency || rate.to_currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={faDollarSign}
                              className="h-4 w-4 text-green-600 mr-1"
                            />
                            <span className="font-medium">
                              {Number(
                                rate.conversionRate ||
                                  rate.conversion_rate ||
                                  0,
                              ).toFixed(4)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={getConversionRateIsActive(rate)}
                            onClick={() =>
                              handleConversionRateToggleActive(rate)
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${getConversionRateIsActive(rate) ? "bg-blue-600" : "bg-gray-300"}`}
                            title="Toggle active status"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${getConversionRateIsActive(rate) ? "translate-x-6" : "translate-x-1"}`}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleConversionRateEdit(rate)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="h-4 w-4"
                              />
                            </button>
                            <button
                              onClick={() =>
                                handleConversionRateDelete(rate.id)
                              }
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="h-4 w-4"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Currency Conversion Rate Modal */}
      {showConversionRateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingConversionRate
                  ? "Edit Conversion Rate"
                  : "Add Conversion Rate"}
              </h3>
              <form onSubmit={handleConversionRateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year
                  </label>
                  <input
                    type="number"
                    required
                    value={conversionRateFormData.year}
                    onChange={(e) =>
                      setConversionRateFormData((prev) => ({
                        ...prev,
                        year:
                          parseInt(e.target.value, 10) ||
                          new Date().getFullYear(),
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Currency
                    </label>
                    <select
                      required
                      value={conversionRateFormData.from_currency}
                      onChange={(e) =>
                        setConversionRateFormData((prev) => ({
                          ...prev,
                          from_currency: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="CAD">CAD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      To Currency
                    </label>
                    <select
                      required
                      value={conversionRateFormData.to_currency}
                      onChange={(e) =>
                        setConversionRateFormData((prev) => ({
                          ...prev,
                          to_currency: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CAD">CAD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conversion Rate
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    required
                    value={conversionRateFormData.conversion_rate}
                    onChange={(e) =>
                      setConversionRateFormData((prev) => ({
                        ...prev,
                        conversion_rate: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={conversionRateFormData.is_active}
                    onChange={(e) =>
                      setConversionRateFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Active</label>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowConversionRateModal(false);
                      setEditingConversionRate(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingConversionRate ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* System Settings Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSetting
                  ? "Edit System Setting"
                  : "Add New System Setting"}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Setting Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.setting_name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          setting_name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Setting Key
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.setting_key}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          setting_key: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={Boolean(editingSetting)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Setting Value
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.setting_value}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          setting_value: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Setting Type
                    </label>
                    <select
                      required
                      value={formData.setting_type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          setting_type: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select type</option>
                      <option value="conversion_rate">Conversion Rate</option>
                      <option value="multiplier">Multiplier</option>
                      <option value="proportion">Proportion</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit
                    </label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={(e) =>
                        setFormData({ ...formData, unit: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingSetting ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettingsManagement;
